import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ConnectedProps, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { faSearch } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { isTablet } from "react-device-detect";
import { connector } from "./redux";
import { ClavaContext } from "../../../config/contexts";
import { showTranslated, translate } from "../../../config/translator";
import { setHead } from "../../../config/utils";
import { League, LeagueCategoryEnum, SubCategory } from "../../../client/api";
import { IOS_STORE_URL } from "../../../config/constants";
import RoundedRectangle from "../RoundedRectangle";
import { pathHome, pathLeague } from "../../../config/routes";
import mockup from "../../../assets/images/mockupNew.png";
import appPlayStoresLogos from "../../../assets/images/appPlayStoreLogos.png";
import AoiSelector from "../AoiSelector";
import { selectAreaOfInterest } from "../../../selectors";
import { hasUserSelectedAoi } from "../../../hooks/useAoiSelector";

type ExtraLeaguesProps = {
  leagueId?: number;
  isBottom?: boolean;
  showFlow?: boolean;
  showInfo?: boolean;
  showLegal?: boolean;
  noOuterPadding?: boolean;
};


const Leagues: React.FC<
  ConnectedProps<typeof connector> & ExtraLeaguesProps & { className?: string }
> = ({
       leagues,
       leagueId,
       isBottom,
       categories,
       getCategories,
       showFlow,
       showInfo,
       aois, aoisStatus, getAois, changeAoi,
       showLegal,
       noOuterPadding,
       className
     }) => {
  const { l, user } = useContext(ClavaContext);
  const navigate = useNavigate();
  const aoi = useSelector(selectAreaOfInterest);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<LeagueCategoryEnum>();
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<
    number | undefined
  >();
  useEffect(() => {
    if (aois.length === 0 && aoisStatus === "idle") {
      getAois();
    }
  }, [aois, aoisStatus, getAois]);
  const [uiStep, setUiStep] = useState<1 | 2 | 3>(1);
  const selectedLeague = useMemo(() => {
    if (!leagueId || leagueId <= 0) return undefined;
    return leagues.find((lea) => lea.id === leagueId);
  }, [leagues, leagueId]);
  const [fadeIn, setFadeIn] = useState(true);
  const [slideDir, setSlideDir] = useState<1 | -1>(1);

  const aoiId = user.areaOfInterest?.id;
  const prevAoiIdRef = React.useRef<number | undefined>(aoiId);

  const groupedAllSubCategories = useMemo(() => {
    const filtered = leagues.filter((league) => {
      const sc = league.subCategory;
      if (!sc) return false;
      const lc = sc.leagueCategory;
      const leagueName = (league?.name ? showTranslated(league.name, l) : "").toLowerCase();
      const subcatName = league.subCategory?.name ? showTranslated(league.subCategory.name, l).toLowerCase() : "";
      const q = search.toLowerCase();
      const inAoi = !aoiId || (league.areasOfInterest ?? []).some((ao) => ao.id === aoiId);
      return inAoi && (!category || lc === category) && (q === "" || leagueName.includes(q) || subcatName.includes(q));
    });
    const map = new Map<number, { subCategory: SubCategory; leagues: League[] }>();
    filtered.forEach((lea) => {
      const sc = lea.subCategory;
      if (!sc) return;
      const found = map.get(sc.id);
      if (found) {
        found.leagues.push(lea);
      } else {
        map.set(sc.id, { subCategory: sc, leagues: [lea] });
      }
    });
    return Array.from(map.values());
  }, [leagues, category, search, l, aoiId]);

  function getListClass(count: number, threshold = 10): string {
    return count <= threshold ? "overflow-visible" : "overflow-auto max-h-96";
  }


  useEffect(() => {
    if (leagueId !== -1) {
      const league = leagues.find((lea) => lea.id === leagueId);
      if (league) {
        setHead(showTranslated(league.name, l));
        setCategory(league.subCategory?.leagueCategory);
        const initialSubCatId = league.subCategory!.id;
        setSelectedSubCategoryId(initialSubCatId);
        setUiStep(initialSubCatId ? 3 : 2);
      }
    } else {
      setCategory(undefined);
      setSelectedSubCategoryId(undefined);
      setUiStep(1);
    }
  }, [leagueId, leagues, l]);

  useEffect(() => {
    if (categories.length === 0) {
      getCategories();
    }
  }, [categories.length, getCategories]);

  useEffect(() => {
    const prev = prevAoiIdRef.current;
    if (prev !== aoiId) {
      prevAoiIdRef.current = aoiId;
      setSearch("");
      setCategory(undefined);
      setSelectedSubCategoryId(undefined);
      setUiStep(1);
      getCategories();
    }
  }, [aoiId, getCategories]);

  // re-trigger animation when step changes
  useEffect(() => {
    setFadeIn(false);
    const id = requestAnimationFrame(() => setFadeIn(true));
    return () => cancelAnimationFrame(id);
  }, [uiStep]);

  // Step 1: categories available for current AOI only
  const availableCategories = useMemo(() => {
    const present = new Set<LeagueCategoryEnum>();
    leagues.forEach((league) => {
      const inAoi =
        !aoiId || (league.areasOfInterest ?? []).some((ao) => ao.id === aoiId);
      if (!inAoi) return;
      const lc = league.subCategory?.leagueCategory;
      if (lc !== undefined) present.add(lc);
    });
    return categories.filter((cat) => present.has(cat.category));
  }, [leagues, aoiId, categories]);

  const selectedCategoryLabel = useMemo(() => {
    const found = categories.find((c) => c.category === category);
    return found?.translation
      ? showTranslated(found.translation, l)
      : undefined;
  }, [categories, category, l]);

  // Step 1
  const step1Count = availableCategories.length;
  const step1Class = getListClass(step1Count);

  // Step 2
  const step2Count = groupedAllSubCategories.length;
  const step2Class = getListClass(step2Count);

  // Step 3
  const selectedGroup = useMemo(
    () =>
      groupedAllSubCategories.find(
        (g) => g.subCategory.id === selectedSubCategoryId
      ),
    [groupedAllSubCategories, selectedSubCategoryId]
  );
  const step3Title = useMemo(() => {
    const name =
      groupedAllSubCategories.find(
        (g) => g.subCategory.id === selectedSubCategoryId
      )?.subCategory.name ?? selectedLeague?.name;
    return name ? showTranslated(name, l) : "";
  }, [groupedAllSubCategories, selectedSubCategoryId, selectedLeague, l]);
  const leaguesSorted = useMemo<League[]>(() => {
    if (!selectedGroup) return [];
    const sc = selectedGroup.subCategory;
    return sc.isCup
      ? selectedGroup.leagues.slice(0, 1)
      : selectedGroup.leagues
        .slice()
        .sort((a, b) =>
          showTranslated(a.name, l).localeCompare(showTranslated(b.name, l))
        );
  }, [selectedGroup, l]);
  const navigateToLeague = useCallback(
    (lid: number) => {
      navigate(pathLeague(lid));
    },
    [navigate]
  );
  const step3Count = selectedGroup ? leaguesSorted.length || 1 : 0;
  const step3Class = getListClass(step3Count);

  const onSearch = useCallback(
    (text: string) => {
      setSearch(text);
      if (uiStep === 1 && text.trim().length > 0) {
        setUiStep(2);
      }
      if (text.trim().length === 0 && category === undefined) {
        setUiStep(1);
      }
    },
    [uiStep, category]
  );

  const onSelectCategory = useCallback(
    (key: LeagueCategoryEnum | undefined) => {
      if (!key) {
        setSlideDir(uiStep > 1 ? 1 : -1);
        setFadeIn(false);
        setCategory(undefined);
        setSelectedSubCategoryId(undefined);
        setUiStep(1);
        requestAnimationFrame(() => setFadeIn(true));
        return;
      }

      const subCategoriesForCategory = groupedAllSubCategories.filter(
        (group) => group.subCategory.leagueCategory === key
      );

      let nextStep: 1 | 2 | 3;
      let selectedSubCatId: number | undefined;

      if (subCategoriesForCategory.length === 1) {
        nextStep = 3;
        selectedSubCatId = subCategoriesForCategory[0].subCategory.id;
      } else {
        nextStep = 2;
        selectedSubCatId = undefined;
      }

      setSlideDir(nextStep > uiStep ? 1 : -1);
      setFadeIn(false);
      setCategory(key);
      setSelectedSubCategoryId(selectedSubCatId);
      setUiStep(nextStep);
      requestAnimationFrame(() => setFadeIn(true));
    },
    [uiStep, groupedAllSubCategories]
  );
  const onSelectSubCategory = useCallback(
    (id: number | undefined) => {
      if (!id) {
        let nextStep: 1 | 2 | 3;
        if (category) {
          const subCategoriesForCategory = groupedAllSubCategories.filter(
            (group) => group.subCategory.leagueCategory === category
          );
          nextStep = subCategoriesForCategory.length === 1 ? 1 : 2;
        } else {
          nextStep = 1;
        }
        setSlideDir(nextStep > uiStep ? 1 : -1);
        setFadeIn(false);
        setSelectedSubCategoryId(undefined);
        setUiStep(nextStep);
        if (nextStep === 1) {
          setCategory(undefined);
        }
        requestAnimationFrame(() => setFadeIn(true));
        return;
      }

      const subCategoryGroup = groupedAllSubCategories.find(
        (group) => group.subCategory.id === id
      );

      if (subCategoryGroup) {
        const isCupSubCategory = subCategoryGroup.subCategory.isCup;
        const singleLeague = subCategoryGroup.leagues.length === 1;

        if (isCupSubCategory || singleLeague) {
          const league = subCategoryGroup.leagues[0];
          if (league) {
            navigateToLeague(league.id);
          } else {
            navigate(pathHome());
          }
          return;
        }
      }

      const nextStep: 1 | 2 | 3 = 3;
      setSlideDir(nextStep > uiStep ? 1 : -1);
      setFadeIn(false);
      setSelectedSubCategoryId(id);
      setUiStep(nextStep);
      requestAnimationFrame(() => setFadeIn(true));
    },
    [uiStep, category, groupedAllSubCategories, navigateToLeague, navigate]
  );
  useEffect(() => {
    if (!leagueId || leagueId <= 0) {
      setSelectedSubCategoryId(undefined);
    }
  }, [leagueId]);

  const renderFlow = (showFlow ?? true) && (!isBottom || isTablet);
  const renderInfo = (showInfo ?? true) && (!isBottom || isTablet);

  if (!isTablet && isBottom && !showLegal) {
    return null;
  }

  const disableOuterPadding = (noOuterPadding ?? false) || isTablet;
  const rowPadClass = isTablet ? "px-0" : "px-2";
  const containerPadClass = disableOuterPadding
    ? "px-0 py-0"
    : "px-6 py-6 xl:px-8 xl:py-8 xl:pt-0";
  return (
    <div
      className={`w-full flex flex-col ${containerPadClass}${
        className ? ` ${className}` : ""
      } league-container`}
    >
      <AoiSelector
        onChange={(key) => {
          const sel = aois.find((a) => a.id.toString() === key);
          if (sel) changeAoi(sel);
        }}
        items={aois.map((a) => ({
          key: a.id.toString(),
          label: showTranslated(a.name, l),
          value: a.id.toString()
        }))}
        transparent
        value={aoi?.id != null ? aoi.id.toString() : ""}
        withoutChoose
        autoOpen={!hasUserSelectedAoi()}
        containerClassName="relative h-14 mt-3 flex-shrink-0 inline-flex items-center rounded-xl bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-4 pr-9"
        className="w-full inline-flex items-center text-font dark:text-font-dark text-sm font-medium bg-transparent border-0 p-0 whitespace-nowrap overflow-hidden text-ellipsis"
      />
      {renderFlow && (
        <RoundedRectangle
          id="league-flow"
          className={`my-3 bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark overflow-visible min-h-0 flex flex-col w-full box-border$${
            isTablet ? " p-2" : " p-3"
          }`}
          hasBorder
          hasTitle={false}
        >
          {(uiStep === 1 || search.trim().length > 0) && (
            <div className="mb-4 relative w-full">
              <FontAwesomeIcon
                icon={faSearch}
                className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-font-600 dark:text-font-600-dark"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={translate("search", l)}
                aria-label={translate("search", l)}
                role="searchbox"
                className="w-full px-3 py-2 pl-9 rounded-lg bg-bg-secondary dark:bg-bg-secondary-dark focus:bg-primary-50 focus:dark:bg-primary-50-dark text-xs font-normal text-font-600 dark:text-font-600-dark placeholder-placeholder dark:placeholder-placeholder-dark border border-light-gray dark:border-light-gray-dark outline-0"
              />
            </div>
          )}
          <div
            key={`step-${uiStep}`}
            className={`flex flex-col min-h-0 transition-transform duration-300 ease-in-out will-change-transform ${
              fadeIn
                ? "opacity-100 translate-x-0"
                : slideDir === 1
                  ? "opacity-0 translate-x-6"
                  : "opacity-0 -translate-x-6"
            }`}
          >
            {uiStep === 1 && (
              <div className="flex flex-col min-h-0">
                <div className="min-h-0">
                  <ul className={`flex flex-col gap-1 w-full ${step1Class}`}>
                    {availableCategories.map((cat) => (
                      <li key={`cat-${cat.category}`}>
                        <button
                          type="button"
                          className="flex w-full items-center justify-between px-2 min-h-7 rounded-md hover:bg-primary-50 dark:hover:bg-primary-50-dark"
                          onClick={() => onSelectCategory(cat.category)}
                        >
                            <span className="text-sm font-medium text-font dark:text-font-dark text-left">
                              {cat.translation
                                ? showTranslated(cat.translation, l)
                                : ""}
                            </span>
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="w-4 h-4"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {uiStep === 2 && (
              <div>
                <div className="flex flex-row items-center gap-2 mb-4 pb-2">
                  {search.trim().length === 0 && (
                    <button
                      type="button"
                      aria-expanded={uiStep >= 2}
                      aria-controls="league-flow"
                      onClick={() => onSelectCategory(undefined)}
                    >
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className="w-4 h-4"
                      />
                    </button>
                  )}
                  <h4 className="text-sm font-regular text-font-600 dark:text-font-600-dark truncate">
                    {selectedCategoryLabel}
                  </h4>
                </div>
                <ul
                  className={`flex flex-col gap-1 ${isTablet ? "pr-0" : "pr-1"} w-full ${step2Class}`}
                >
                  {groupedAllSubCategories.map(({ subCategory: sc, leagues: groupLeagues }) => {
                    const singleLeague = groupLeagues.length === 1 ? groupLeagues[0] : undefined;
                    const label = singleLeague
                      ? (singleLeague.name ? showTranslated(singleLeague.name, l) : "")
                      : (sc?.name ? showTranslated(sc.name, l) : "");

                    return (
                      <li key={`macro-${sc.id}`}>
                        <button
                          type="button"
                          className={`flex w-full items-center justify-between ${rowPadClass} py-1 rounded-md hover:bg-primary-50 dark:hover:bg-primary-50-dark`}
                          onClick={() => onSelectSubCategory(sc.id)}
                        >
                          <span className="text-xs font-medium text-font dark:text-font-dark text-left">
                            {label}
                          </span>
                          {search.trim().length === 0 && (
                            <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {uiStep === 3 && (
              <div>
                <div className="flex flex-row items-center gap-2 mb-4 pb-2">
                  {search.trim().length === 0 && (
                    <button
                      type="button"
                      aria-expanded={uiStep === 3}
                      aria-controls="league-flow"
                      onClick={() => onSelectSubCategory(undefined)}
                    >
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className="w-4 h-4"
                      />
                    </button>
                  )}
                  <h4 className="text-sm font-regular text-font-600 dark:text-font-600-dark truncate">
                    {step3Title}
                  </h4>
                </div>
                {selectedGroup && (
                  <ul className={`flex flex-col gap-1 w-full ${step3Class}`}>
                    {selectedGroup.subCategory.isCup ? (
                      <li>
                        <button
                          type="button"
                          onClick={() => {
                            const first = selectedGroup.leagues[0];
                            if (first) navigateToLeague(first.id);
                            else navigate(pathHome());
                          }}
                          className={`flex w-full items-center justify-between ${rowPadClass} py-1 rounded-md hover:bg-primary-50 dark:hover:bg-primary-50-dark`}
                        >
                            <span className="text-xs font-medium text-font dark:text-font-dark text-left">
                              {selectedGroup.subCategory?.name
                                ? showTranslated(
                                  selectedGroup.subCategory.name,
                                  l
                                )
                                : ""}
                            </span>
                          {search.trim().length === 0 && (
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className="w-4 h-4"
                            />
                          )}
                        </button>
                      </li>
                    ) : (
                      leaguesSorted.map((lea) => (
                        <li key={`lea-${lea.id}`}>
                          <button
                            type="button"
                            onClick={() => navigateToLeague(lea.id)}
                            aria-current={
                              leagueId === lea.id ? "page" : undefined
                            }
                            className={`flex w-full items-center justify-between ${rowPadClass} py-1 rounded-md hover:bg-primary-50 dark:hover:bg-primary-50-dark ${
                              leagueId === lea.id
                                ? "bg-primary-50 dark:bg-primary-50-dark"
                                : ""
                            }`}
                          >
                              <span className="text-xs font-medium text-font dark:text-font-dark text-left">
                                {lea?.name ? showTranslated(lea.name, l) : ""}
                              </span>
                            {search.trim().length === 0 && (
                              <FontAwesomeIcon
                                icon={faChevronRight}
                                className="w-4 h-4"
                              />
                            )}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        </RoundedRectangle>
      )}
      {renderInfo && (
        <RoundedRectangle
          className="my-4 bg-white dark:bg-bg-secondary-dark overflow-hidden py-5 px-6 xl:py-4 xl:px-8 relative border border-light-gray dark:border-light-gray-dark w-full"
          hasBorder
          hasTitle={false}
        >
          <div className="mx-0">
            <img
              alt="App"
              title="App"
              src={mockup}
              className="w-full max-w-sm mt-0 mx-auto"
            />
            <div className="w-full mt-2 flex items-center justify-center">
              <img
                src={appPlayStoresLogos}
                alt="App Store and Play Store"
                title="App Store and Play Store"
                className="h-8 w-auto xl:h-10"
              />
            </div>
            <div className="w-full mt-3 flex justify-center">
              <a
                href={IOS_STORE_URL}
                className="w-full mx-2 xl:mx-3 rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-primary active:bg-btn-active hover:bg-btn-active text-font-dark"
              >
                <span className="text-xs font-semibold px-4 whitespace-nowrap">
                  {translate("downloadNow", l)}
                </span>
              </a>
            </div>
          </div>
        </RoundedRectangle>
      )}
    </div>
  );
};

export default connector(Leagues);
